import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, throwError } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';

import { UserService } from '../../user/services/user.service';
import { TokenStorageService } from './token-storage.service';
import { Oauth2Service } from './oauth2.service';
import { AuthErrorHandlerService } from './auth-error-handler.service';
import { UserModel } from '../../../models/user.model';

export enum SessionState {
  UNKNOWN = 'unknown',
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated',
  RESTORING = 'restoring'
}

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly _userService = inject(UserService);
  private readonly _tokenStorage = inject(TokenStorageService);
  private readonly _oauth2Service = inject(Oauth2Service);
  private readonly _authErrorHandler = inject(AuthErrorHandlerService);
  
  private readonly _sessionState = new BehaviorSubject<SessionState>(SessionState.UNKNOWN);
  
  readonly sessionState$ = this._sessionState.asObservable();
  readonly isAuthenticated$ = this.sessionState$.pipe(
    map(state => state === SessionState.AUTHENTICATED)
  );
  readonly isRestoring$ = this.sessionState$.pipe(
    map(state => state === SessionState.RESTORING)
  );

  async tryRestoreSession(): Promise<void> {
    if (this._sessionState.value === SessionState.RESTORING) {
      return;
    }

    this._sessionState.next(SessionState.RESTORING);

    try {
      await this._refreshTokenFromCookie();
      await this._fetchUserData();
      this._sessionState.next(SessionState.AUTHENTICATED);
    } catch {
      this._sessionState.next(SessionState.UNAUTHENTICATED);
    }
  }

  getCurrentUser(): UserModel | null {
    return this._userService.getUser().value;
  }

  getSessionState(): SessionState {
    return this._sessionState.value;
  }

  isAuthenticated(): boolean {
    return this._sessionState.value === SessionState.AUTHENTICATED;
  }

  setAuthenticated(): void {
    this._sessionState.next(SessionState.AUTHENTICATED);
    console.log('Session marked as authenticated');
  }

  clearSession(): void {
    this._tokenStorage.clearToken();
    this._userService.getUser().next(null);
    this._sessionState.next(SessionState.UNAUTHENTICATED);
    console.log('Session cleared');
  }

  private async _refreshTokenFromCookie(): Promise<void> {
    try {
      // Check if we're in a user change process by looking for OAuth parameters
      const urlParams = new URLSearchParams(window.location.search);
      const hasOAuthParams = urlParams.has('_et') || urlParams.has('code') || urlParams.has('state');
      
      if (hasOAuthParams) {
        // Don't try to refresh during OAuth callback or user change
        throw new Error('OAuth flow in progress - skipping token refresh');
      }
      
      const refreshResponse = await firstValueFrom(this._oauth2Service.refreshToken());
      
      if (!refreshResponse?.accessToken) {
        const authError = this._authErrorHandler.handleAuthError(new Error('No valid refresh token'));
        throw authError;
      }
      
      this._tokenStorage.setAccessToken(refreshResponse.accessToken);
    } catch (error) {
      const authError = this._authErrorHandler.handleAuthError(error);
      if (authError.shouldLogout) {
        this._handleSessionFailure();
      }
      throw authError;
    }
  }

  private async _fetchUserData(): Promise<void> {
    try {
      const user = await firstValueFrom(
        this._userService.fetchUser().pipe(
          catchError((error) => {
            const authError = this._authErrorHandler.handleAuthError(error);
            this._handleSessionFailure();
            return throwError(() => authError);
          })
        )
      );

      if (!user) {
        const authError = this._authErrorHandler.handleAuthError(new Error('Failed to fetch user data'));
        throw authError;
      }
    } catch (error) {
      const authError = this._authErrorHandler.handleAuthError(error);
      this._handleSessionFailure();
      throw authError;
    }
  }

  private _handleSessionFailure(): void {
    this._tokenStorage.clearToken();
    this._userService.getUser().next(null);
  }
}